import { useState, useEffect } from 'react'
import { User, Building2, Plus, Trash2, ArrowLeft } from 'lucide-react'
import { COUNTRIES, INDUSTRIES } from '../lib/localization'
import { getCountryIdTypes } from '../lib/idTypes'
import { profilesApi, companiesApi } from '../lib/api'
import { useToast } from '../lib/ToastContext'
import { supabase } from '../lib/supabase'

const EMPTY_KYC = { fullName: '', idType: '', idNumber: '', phone: '', address: '' }
const EMPTY_BIZ = {
  businessName: '',
  country: 'Nigeria',
  corporateAddress: '',
  industry: INDUSTRIES[0],
  website: '',
}

export default function ProfileScreen({ profile, onSave, companyId, userId, onNavigate }) {
  const [tab, setTab] = useState('kyc')
  const [kyc, setKyc] = useState(profile?.kyc || EMPTY_KYC)
  const [biz, setBiz] = useState(profile?.business || EMPTY_BIZ)
  const [directors, setDirectors] = useState(profile?.directors || [])
  const [newDirector, setNewDirector] = useState('')
  const [saved, setSaved] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (!companyId) return
    let mounted = true
    ;(async () => {
      const { data } = await companiesApi.get(companyId)
      if (!mounted) return
      if (data?.directors && Array.isArray(data.directors) && data.directors.length > 0) {
        setDirectors(data.directors.map((name, i) => ({ id: `d-${i}`, name })))
      }
      if (data?.name) setBiz((prev) => ({ ...prev, businessName: data.name, country: data.country || prev.country, corporateAddress: data.corporate_address || prev.corporateAddress, industry: data.industry || prev.industry }))
    })()
    return () => { mounted = false }
  }, [companyId])

  const setK = (k, v) => setKyc((p) => ({ ...p, [k]: v }))
  const setB = (k, v) => setBiz((p) => ({ ...p, [k]: v }))

  const addDirector = () => {
    const name = newDirector.trim()
    if (!name) return
    setDirectors((prev) => [...prev, { id: `d-${Date.now()}`, name }])
    setNewDirector('')
  }

  const removeDirector = (id) => setDirectors((prev) => prev.filter((d) => d.id !== id))

  const save = async () => {
    onSave?.({ kyc, business: biz, directors })
    try {
      if (userId) {
        await profilesApi.update(userId, {
          full_name: kyc.fullName,
          phone: kyc.phone,
          id_type: kyc.idType,
          id_number: kyc.idNumber,
          home_address: kyc.address,
        })
      }
      if (companyId) {
        await companiesApi.update(companyId, {
          name: biz.businessName,
          country: biz.country,
          corporate_address: biz.corporateAddress,
          industry: biz.industry,
          directors: directors.map((d) => d.name),
        })
      }
      toast('Profile saved successfully.', 'success')
    } catch (err) {
      toast('Failed to save profile: ' + (err.message || 'Unknown error'), 'error')
    }
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const idTypes = getCountryIdTypes(biz.country)

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          {/* {onNavigate && (
            <button onClick={() => onNavigate('dashboard')} className="btn-ghost text-xs flex items-center gap-1.5 mb-4">
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
          )} */}
          <h1 className="text-2xl font-bold text-ink">Profile</h1>
          <p className="text-sm text-muted mt-1">Founder KYC and business profile.</p>
        </div>
        {saved && (
          <span className="text-xs font-semibold text-emerald-700">Saved</span>
        )}
      </div>

      <div className="flex border-b border-neutral-200">
        <TabButton active={tab === 'kyc'} onClick={() => setTab('kyc')} icon={User} label="Founder KYC" />
        <TabButton active={tab === 'business'} onClick={() => setTab('business')} icon={Building2} label="Business Profile" />
      </div>

      {tab === 'kyc' ? (
        <div className="surface p-7 max-w-2xl">
          <div className="space-y-5">
            <div>
              <label className="label-above">Full Legal Name</label>
              <input type="text" value={kyc.fullName} onChange={(e) => setK('fullName', e.target.value)} className="input-flat" placeholder="As it appears on your ID" />
            </div>
            <div>
              <label className="label-above">Government ID Type</label>
              <select value={kyc.idType} onChange={(e) => setK('idType', e.target.value)} className="input-flat">
                <option value="">Select ID type</option>
                {idTypes.map((t) => (<option key={t}>{t}</option>))}
              </select>
            </div>
            <div>
              <label className="label-above">ID Number</label>
              <input type="text" value={kyc.idNumber} onChange={(e) => setK('idNumber', e.target.value)} className="input-flat font-mono" placeholder="Enter your government ID number" />
            </div>
            <div>
              <label className="label-above">Phone Number</label>
              <input type="tel" value={kyc.phone} onChange={(e) => setK('phone', e.target.value)} className="input-flat font-mono" placeholder="+234 800 000 0000" />
            </div>
            <div>
              <label className="label-above">Home Address</label>
              <input type="text" value={kyc.address} onChange={(e) => setK('address', e.target.value)} className="input-flat" placeholder="Street, city, state" />
            </div>
          </div>
          <div className="mt-7">
            <button onClick={save} className="btn-primary">Save Changes</button>
          </div>
        </div>
      ) : (
        <div className="surface p-7 max-w-2xl space-y-7">
          <div className="space-y-5">
            <div>
              <label className="label-above">Registered Business Name</label>
              <input type="text" value={biz.businessName} onChange={(e) => setB('businessName', e.target.value)} className="input-flat" placeholder="Acme Innovations Ltd" />
            </div>
            <div>
              <label className="label-above">Operating Country</label>
              <select value={biz.country} onChange={(e) => setB('country', e.target.value)} className="input-flat">
                {COUNTRIES.map((c) => (<option key={c}>{c}</option>))}
              </select>
            </div>
            <div>
              <label className="label-above">Corporate Address</label>
              <input type="text" value={biz.corporateAddress} onChange={(e) => setB('corporateAddress', e.target.value)} className="input-flat" placeholder="Registered office address" />
            </div>
            <div>
              <label className="label-above">Industry</label>
              <select value={biz.industry} onChange={(e) => setB('industry', e.target.value)} className="input-flat">
                {INDUSTRIES.map((i) => (<option key={i}>{i}</option>))}
              </select>
            </div>
            <div>
              <label className="label-above">Website URL</label>
              <input type="text" value={biz.website} onChange={(e) => setB('website', e.target.value)} className="input-flat font-mono" placeholder="https://yourcompany.com" />
            </div>
          </div>

          <div className="pt-5 border-t border-neutral-200">
            <label className="label-above">Directors</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newDirector}
                onChange={(e) => setNewDirector(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDirector())}
                className="input-flat"
                placeholder="Add a director by name"
              />
              <button onClick={addDirector} className="btn-primary text-xs flex items-center gap-1.5 shrink-0">
                <Plus size={14} /> Add
              </button>
            </div>
            <ul className="divide-y divide-neutral-200 border border-neutral-200">
              {directors.map((d) => (
                <li key={d.id} className="px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-ink">{d.name}</span>
                  <button onClick={() => removeDirector(d.id)} className="text-neutral-400 hover:text-rose-600" aria-label={`Remove ${d.name}`}>
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
              {directors.length === 0 && (
                <li className="px-4 py-4 text-center text-xs text-muted">No directors added yet.</li>
              )}
            </ul>
          </div>

          <div>
            <button onClick={save} className="btn-primary">Save Changes</button>
          </div>
        </div>
      )}
    </div>
  )
}

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-brand ${
        active ? 'text-brand' : 'text-neutral-400 hover:text-neutral-600'
      }`}
    >
      <Icon size={15} />
      {label}
      {active && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-brand" />}
    </button>
  )
}
